import { findParentNodeClosestToPos } from "@tiptap/core";
import classNames from "classnames";
import React, { Suspense, useContext, useEffect, useState } from "react";
import {
  TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE,
  TIPTAP_COLLAPSIBLE_ITEM_TYPE,
  TIPTAP_COLLAPSIBLE_LIST_TYPE
} from "./../../ricos-content";
import {
  EditorContext,
  EditorQueryContext,
  PluginsEventsContext,
  RicosContext
} from "./../../ricos-context";
import ExpandCollapseButton from "../components/ExpandCollapseButton";
import NewPairButton from "../components/NewPairButton";
import {
  COLLAPSIBLE_LIST_ITEM,
  COLLAPSIBLE_LIST_ITEM_BODY,
  COLLAPSIBLE_LIST_ITEM_TITLE,
  ROOT
} from "../data-hooks";
import { DndIcon } from "../icons";
import collapsibleListStyles from "../statics/styles/collapsible-list-component.rtlignore-scss";
import collapsibleListItemStyles from "../statics/styles/collapsible-list-pair.rtlignore-scss";
import { supportedPluginTypes } from "./defaults";
import { isInCollapsibleList } from "./utils";
const FloatingAddPluginMenu = React.lazy(() => import("./lazy-FloatingAddPluginMenu"));
const preventFocusJumpToFirstNode = (event) => {
  event.preventDefault();
  event.stopPropagation();
};
const CollapsibleList = ({
  editor,
  node,
  NodeViewContent,
  componentData,
  getPos
}) => {
  const { t, languageDir } = useContext(RicosContext);
  const editorQuery = useContext(EditorQueryContext);
  const pluginsEvents = useContext(PluginsEventsContext);
  const { isNodeSelected } = editorQuery.query;
  const styles = collapsibleListStyles;
  const pos = getPos();
  const [isCollapsibleListSelected, setIsCollapsibleListSelected] = useState(
    isNodeSelected(node.attrs.id)
  );
  const direction = componentData?.direction || languageDir;
  useEffect(() => {
    editor.on("selectionUpdate", handleSelectionUpdate);
    return () => {
      editor.off("selectionUpdate", handleSelectionUpdate);
    };
  }, []);
  useEffect(() => {
    if (!isCollapsibleListSelected) {
      pluginsEvents?.publishPluginLayoutShift({ nodeId: node.attrs.id });
    }
  }, [isCollapsibleListSelected]);
  const handleSelectionUpdate = () => {
    setIsCollapsibleListSelected(isNodeSelected(node.attrs.id));
  };
  const addNewPair = (e) => {
    e.preventDefault();
    e.stopPropagation();
    editor.commands.addCollapsibleListItem(pos + node.nodeSize);
  };
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(styles[direction.toLowerCase()], styles.listDimensions),
      "data-hook": ROOT
    },
    /* @__PURE__ */ React.createElement(NodeViewContent, { className: classNames(styles[direction.toLowerCase()], styles.item) }),
    isCollapsibleListSelected && /* @__PURE__ */ React.createElement(
      NewPairButton,
      {
        onClick: addNewPair,
        label: t("CollapsibleList_ShownText_Add_Placeholder")
      }
    )
  );
};
const CollapsibleListItem = ({ editor, node, NodeViewContent }) => {
  const styles = collapsibleListItemStyles;
  const { t, languageDir } = useContext(RicosContext);
  const {
    adapter: { tiptapEditor }
  } = useContext(EditorContext);
  const $pos = editor.state.selection.$to;
  const inCollapsibleList = isInCollapsibleList($pos, node.attrs.id);
  const isExpanded = !!node.attrs.isExpanded;
  const toggleCollapsibleItem = () => {
    tiptapEditor.chain().focus().toggleCollapsibleItem(node.attrs.id);
  };
  const [mouseDown, setMouseDown] = useState(false);
  const dir = findParentNodeClosestToPos($pos, (node2) => node2.type.name === TIPTAP_COLLAPSIBLE_LIST_TYPE)?.node?.attrs?.direction || languageDir.toUpperCase();
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: styles.itemContainer,
      "data-hook": `${COLLAPSIBLE_LIST_ITEM} ${isExpanded ? "expanded" : "collapsed"}`
    },
    inCollapsibleList && // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(styles.dndIcon, styles[dir], mouseDown && styles.mouseDown),
        draggable: "true",
        contentEditable: "false",
        "data-drag-handle": true,
        onDragEnd: () => setMouseDown(false),
        onMouseUp: () => setMouseDown(false),
        onMouseDown: () => setMouseDown(true)
      },
      /* @__PURE__ */ React.createElement(DndIcon, null)
    ),
    /* @__PURE__ */ React.createElement("div", { className: styles.expandIcon }, /* @__PURE__ */ React.createElement(
      ExpandCollapseButton,
      {
        isExpanded,
        onClick: toggleCollapsibleItem,
        ariaLabel: isExpanded ? t("CollapsibleListPlugin_ExpandButton_AriaLabel") : t("CollapsibleListPlugin_CollapseButton_AriaLabel")
      }
    )),
    /* @__PURE__ */ React.createElement(NodeViewContent, { className: collapsibleListItemStyles.innerEditor })
  );
};
const CollapsibleListItemTitle = ({ NodeViewContent }) => {
  return /* @__PURE__ */ React.createElement(
    "div",
    {
      className: classNames(
        collapsibleListItemStyles.container,
        collapsibleListItemStyles.titleContainer
      )
    },
    /* @__PURE__ */ React.createElement(
      NodeViewContent,
      {
        contentEditable: "true",
        className: collapsibleListItemStyles.innerEditor,
        "data-hook": COLLAPSIBLE_LIST_ITEM_TITLE
      }
    )
  );
};
const CollapsibleListItemBody = ({
  NodeViewContent,
  editor,
  getPos,
  node
}) => {
  const $pos = editor.view.state.doc.resolve(getPos());
  const { languageDir } = useContext(RicosContext);
  const editorQuery = useContext(EditorQueryContext);
  const isShown = findParentNodeClosestToPos(
    $pos,
    (node2) => node2.type.name === TIPTAP_COLLAPSIBLE_ITEM_TYPE
  )?.node?.attrs?.isExpanded;
  const isFloatingMenuVisible = React.useCallback(() => {
    const selectionContainsTitle = (() => {
      const nodes = editorQuery.selection().doc?.children?.[0]?.descendants(
        (node2) => node2.type() === TIPTAP_COLLAPSIBLE_ITEM_TITLE_TYPE
      );
      return nodes.length > 0;
    })();
    const isCurrentNodeChildIsSingleTextBlock = (() => editorQuery.query.isChildSingleTextBlock(node.attrs.id))();
    return !selectionContainsTitle && isCurrentNodeChildIsSingleTextBlock;
  }, [node]);
  const dir = findParentNodeClosestToPos(
    $pos,
    (node2) => node2.type.name === TIPTAP_COLLAPSIBLE_LIST_TYPE
  )?.node?.attrs?.direction?.toLowerCase() || languageDir;
  return isShown ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Suspense, { fallback: /* @__PURE__ */ React.createElement("div", null) }, /* @__PURE__ */ React.createElement(
    "div",
    {
      className: collapsibleListItemStyles.floatingAddPluginMenuWrapper,
      onClick: preventFocusJumpToFirstNode,
      onMouseDown: preventFocusJumpToFirstNode,
      onKeyDown: preventFocusJumpToFirstNode
    },
    /* @__PURE__ */ React.createElement(
      FloatingAddPluginMenu,
      {
        dir,
        supportedButtonIds: supportedPluginTypes,
        isVisible: isFloatingMenuVisible,
        nodeId: node.attrs.id
      }
    )
  )), /* @__PURE__ */ React.createElement("div", { className: collapsibleListItemStyles.container }, /* @__PURE__ */ React.createElement(
    NodeViewContent,
    {
      contentEditable: "true",
      className: collapsibleListItemStyles.innerEditor,
      "data-hook": COLLAPSIBLE_LIST_ITEM_BODY
    }
  ))) : null;
};
export {
  CollapsibleList,
  CollapsibleListItem,
  CollapsibleListItemBody,
  CollapsibleListItemTitle
};
//# sourceMappingURL=component.js.map
